import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useState } from 'react';
import { logTermsAcceptance, maskEmail } from '@ibk/shared-utils';
import { CpfValidation } from './components/CpfValidation';
import { FinishedOnboarding } from './components/FinishedOnboarding';
import { useAccountData, useEmailValidation, usePhoneValidation } from './components/hooks';
import { PasswordCreation } from './components/PasswordCreation';
import { TermsOfUse } from './components/TermsOfUse';
import { TokenValidation } from './components/TokenValidation';
import { hasRequiredOnboardingContacts } from './hasRequiredOnboardingContacts';
export function Onboarding({ domain, onBackToLogin }) {
    const [currentStep, setCurrentStep] = useState('cpf');
    const [tokenSource, setTokenSource] = useState('email');
    const [hasError, setHasError] = useState(false);
    const [isContactSetupLoading, setIsContactSetupLoading] = useState(false);
    const { email, phone, setMaskedRecipient } = useAccountData();
    const { handleSaveDevice, isLoading: isEmailDeviceLoading } = useEmailValidation();
    const { handleSendPhoneToken, isLoading: isPhoneTokenSending } = usePhoneValidation();
    const handleCpfContinue = useCallback(async () => {
        if (!hasRequiredOnboardingContacts(email, phone)) {
            return;
        }
        setIsContactSetupLoading(true);
        try {
            const result = await handleSaveDevice();
            const apiMaskedRecipient = result?.result?.maskedRecipient;
            setMaskedRecipient(apiMaskedRecipient ?? maskEmail(email));
            setTokenSource('email');
            setCurrentStep('token');
        }
        catch (cause) {
            logTermsAcceptance('onboarding.email_validation.continue_failed', { description: 'Falha ao registrar dispositivo no onboarding.' }, 'error', cause);
        }
        finally {
            setIsContactSetupLoading(false);
        }
    }, [email, phone, handleSaveDevice, setMaskedRecipient]);
    const handleTokenBack = () => {
        if (tokenSource === 'phone') {
            setTokenSource('email');
            return;
        }
        setCurrentStep('cpf');
    };
    const handleTokenContinue = useCallback(async () => {
        if (tokenSource === 'phone') {
            setCurrentStep('password');
            return;
        }
        setIsContactSetupLoading(true);
        try {
            await handleSendPhoneToken();
            setTokenSource('phone');
        }
        catch (cause) {
            logTermsAcceptance('onboarding.phone_validation.continue_failed', { description: 'Falha ao enviar token de telefone no onboarding.' }, 'error', cause);
        }
        finally {
            setIsContactSetupLoading(false);
        }
    }, [handleSendPhoneToken, tokenSource]);
    const handlePasswordBack = () => {
        setTokenSource('phone');
        setCurrentStep('token');
    };
    const handlePasswordContinue = () => {
        setCurrentStep('terms');
    };
    const handleTermsBack = () => {
        setCurrentStep('password');
    };
    const handleTermsContinue = (termsHasError) => {
        setHasError(termsHasError);
        setCurrentStep('finished');
    };
    const isCpfContinuing = isContactSetupLoading || isEmailDeviceLoading;
    return (_jsxs("div", { "data-testid": 'onboarding-wrapper', children: [currentStep === 'cpf' ? (_jsx(CpfValidation, { domain: domain, onBackToLogin: onBackToLogin, onContinue: handleCpfContinue, isContinuing: isCpfContinuing })) : null, currentStep === 'token' ? (_jsx(TokenValidation, { onBack: handleTokenBack, onContinue: handleTokenContinue, source: tokenSource, isTransitioning: isContactSetupLoading || isPhoneTokenSending })) : null, currentStep === 'password' ? (_jsx(PasswordCreation, { onBack: handlePasswordBack, onContinue: handlePasswordContinue })) : null, currentStep === 'terms' ? _jsx(TermsOfUse, { onBack: handleTermsBack, onContinue: handleTermsContinue }) : null, currentStep === 'finished' ? _jsx(FinishedOnboarding, { hasError: hasError }) : null] }));
}
